import { TableFilterState, TableSortingState } from '@ibsys/kit';
import type { PhraseSearchRole, SearchEntityType, SearchQueryRequest } from '@/entities/search';
import type { SearchConversationRequest, SearchDialogsPage } from '@/entities/search';
import { tableSorting2Query } from '@/shared/lib';
import { buildBaseRequest } from './base';
import { isCommentSearchActive } from './contentSearch';
import { buildConversationFilters, buildConversationSort, mapDialogsPageForConversationApi } from './conversation';
import { buildRuleFilters } from './rule';
import { buildKnowledgeFilters } from './knowledge';
import { buildKnowledgeSetFilters } from './knowledgeSet';
import type { SearchQueryParams } from './types';

export type { SearchQueryParams } from './types';
export { buildSearchQueryParams } from './base';

const filterBuilders: Record<SearchEntityType, (filters: TableFilterState) => Record<string, unknown> | undefined> = {
  conversation: buildConversationFilters,
  rule: buildRuleFilters,
  knowledge: buildKnowledgeFilters,
  knowledge_set: buildKnowledgeSetFilters,
};

const buildFilters = (entityType: SearchEntityType, filters: TableFilterState) => filterBuilders[entityType](filters);

/**
 * Добавляет сортировку к запросу.
 * Для conversation — вложенная структура (спецификация: frontend/conversation/sorting.md).
 * Для остальных сущностей — плоский массив SearchSortItem.
 */
const applySort = (request: SearchQueryRequest, entityType: SearchEntityType, sorting: TableSortingState): void => {
  if (!sorting?.length) return;

  if (entityType === 'conversation') {
    const conversationSort = buildConversationSort(sorting);
    if (conversationSort) {
      (request as SearchConversationRequest).sort = conversationSort;
    }
  } else {
    const searchSort = tableSorting2Query(sorting);
    if (searchSort.length > 0) {
      (request as Exclude<SearchQueryRequest, SearchConversationRequest>).sort = searchSort;
    }
  }
};

/**
 * Опции FTS-поиска по содержимому диалога для conversation (spec: api/search/query.md).
 * Поля взаимоисключающие — discriminated union гарантирует на уровне типа, что одновременно
 * может быть передано не более одного непустого поля. Серверная валидация это дополнительно
 * гарантирует (safety net).
 */
export type ConversationContentSearchOptions =
  | {
      /** FTS-поиск по репликам. Пустая строка эквивалентна отсутствию (фильтр не применяется) */
      phraseSearch?: string;
      /** Сторона говорящего; уходит в запрос только при непустом `phraseSearch` (отсутствие — обе стороны) */
      phraseSearchRole?: PhraseSearchRole;
      /**
       * Ограничение по операторам; уходит в запрос только при непустом `phraseSearch` **и**
       * `phraseSearchRole=employee` (пустой набор — все консультанты)
       */
      phraseSearchEmployeeIds?: number[];
      commentSearch?: never;
      commentSearchActive?: never;
      commentSearchAuthorIds?: never;
    }
  | {
      phraseSearch?: never;
      phraseSearchRole?: never;
      phraseSearchEmployeeIds?: never;
      /** FTS-поиск по комментариям. Пустая строка запросом не считается — отбор идёт по наличию комментария */
      commentSearch?: string;
      /**
       * Режим поиска по комментариям активен. Уходит в запрос, пока режим включён, в том числе
       * с пустым `commentSearch`: список тогда показывает диалоги с комментариями
       */
      commentSearchActive?: boolean;
      /** Ограничение по авторам комментариев; уходит в запрос, пока режим активен, в том числе при пустом запросе (пустой набор — все авторы) */
      commentSearchAuthorIds?: number[];
    };

/**
 * Мерджит phraseSearch/commentSearch в `filters` запроса conversation (spec: api/search/query.md).
 * Поиск по репликам применяется только с непустым запросом; поиск по комментариям — пока режим
 * активен, в том числе с пустым запросом (тогда отбор идёт по наличию комментария).
 * Поля двух режимов вместе не уходят — режимы взаимоисключающие.
 */
const applyContentSearch = (
  request: SearchConversationRequest,
  options?: ConversationContentSearchOptions,
): void => {
  if (!options) return;
  const phrase = options.phraseSearch?.trim();
  const comment = options.commentSearch?.trim();
  const isCommentModeActive = isCommentSearchActive(options);
  if (!phrase && !isCommentModeActive) return;
  // Роль говорящего осмысленна только при непустом поиске по репликам.
  const phraseSearchRole = phrase ? options.phraseSearchRole : undefined;
  // Ограничение по операторам осмысленно только вместе с поиском по репликам стороны консультанта.
  const phraseSearchEmployeeIds =
    phrase && phraseSearchRole === 'employee' ? options.phraseSearchEmployeeIds : undefined;
  // Ограничение по авторам осмысленно, пока активен режим по комментариям, — с запросом и без него.
  const commentSearchAuthorIds = isCommentModeActive ? options.commentSearchAuthorIds : undefined;
  request.filters = {
    ...(request.filters ?? {}),
    ...(phrase
      ? {
          phraseSearch: phrase,
          ...(phraseSearchRole ? { phraseSearchRole } : {}),
          ...(phraseSearchEmployeeIds?.length ? { phraseSearchEmployeeIds } : {}),
        }
      : {}),
    ...(isCommentModeActive
      ? {
          commentSearchActive: true,
          ...(comment ? { commentSearch: comment } : {}),
          ...(commentSearchAuthorIds?.length ? { commentSearchAuthorIds } : {}),
        }
      : {}),
  };
};

/**
 * Формирует SearchQueryRequest без limit/offset — сам срез выборки: запрос, фильтры,
 * сортировка, колонки и поиск по содержимому. Общая основа постраничного поиска
 * (`buildSearchRequest`) и выгрузки списка файлом: срез у них обязан совпадать.
 */
export const buildSearchRequestParams = (
  entityType: SearchEntityType,
  query: SearchQueryParams,
  options?: {
    filters?: TableFilterState;
    sorting?: TableSortingState;
    dialogsPage?: SearchDialogsPage;
    /** Поиск по содержимому диалога (phraseSearch/commentSearch, только для conversation) */
    contentSearchOptions?: ConversationContentSearchOptions;
    /**
     * Оставить колонки участников раздельными по ролям, как их запросил UI. Нужно потребителям,
     * которые рисуют «Клиента» и «Консультанта» отдельными колонками сами (выгрузка файлом);
     * поиск же ждёт объединённый состав и разводит роли по колонкам уже на экране.
     */
    splitPersonColumns?: boolean;
  },
): Omit<SearchQueryRequest, 'limit' | 'offset'> => {
  const { filters = [], sorting = [], dialogsPage, contentSearchOptions, splitPersonColumns } = options ?? {};

  const params: Omit<SearchQueryRequest, 'limit' | 'offset'> = {
    ...buildBaseRequest(entityType, query),
  };

  const searchFilters = buildFilters(entityType, filters);
  if (searchFilters) {
    params.filters = searchFilters as SearchQueryRequest['filters'];
  }

  applySort(params as SearchQueryRequest, entityType, sorting);

  if (entityType === 'conversation' && dialogsPage != null && Object.keys(dialogsPage).length > 0) {
    (params as SearchConversationRequest).dialogsPage = splitPersonColumns
      ? dialogsPage
      : mapDialogsPageForConversationApi(dialogsPage);
  }

  if (entityType === 'conversation') {
    applyContentSearch(params as SearchConversationRequest, contentSearchOptions);
  }

  return params;
};

/**
 * Формирует полный SearchQueryRequest из состояния поиска и параметров таблицы:
 * срез выборки плюс постраничность.
 */
export const buildSearchRequest = (
  entityType: SearchEntityType,
  query: SearchQueryParams,
  options: {
    limit: number;
    offset: number;
    filters?: TableFilterState;
    sorting?: TableSortingState;
    dialogsPage?: SearchDialogsPage;
    /** Поиск по содержимому диалога (phraseSearch/commentSearch, только для conversation) */
    contentSearchOptions?: ConversationContentSearchOptions;
  },
): SearchQueryRequest => {
  const { limit, offset, ...params } = options;

  return {
    ...buildSearchRequestParams(entityType, query, params),
    limit,
    offset,
  } as SearchQueryRequest;
};
